import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useAccountSettingsStore = defineStore('accountSettings', () => {
  // Strict role list and helpers (aligned with Autofocus Highlights Admin)
  const VALID_ROLES = new Set([
    'company_user',
    'company_admin',
    'company_suadmin',
    'company_owner',
    'client_user',
    'client_manager',
    'client_admin',
  ])

  const normalizeRole = (value: string): string =>
    String(value || '')
      .trim()
      .toLowerCase()
  const deriveIsClient = (role: string): boolean => role.startsWith('client_')

  const accountId = ref('')
  const isClientUser = ref(false)
  const sessionToken = ref('')
  const role = ref('')
  const inventoryIds = ref<number[]>([])
  const accountSettings = ref<IAccountSettings | null>(null)

  const setAccountSettings = (settings: IAccountSettings | null) => {
    // Set Account ID
    setAccountId(settings?.juid ?? '')

    // Set Inventory IDs
    const inventoryIds = settings?.a5Account ?? []
    const ensuredArrayOfIds = Array.isArray(inventoryIds)
      ? inventoryIds
      : String(inventoryIds)
          .split(',')
          .map((id: string) => id.trim())
    const ensuredNumberIds = ensuredArrayOfIds.map((id: string | number) =>
      Number(id),
    )
    setInventoryId(ensuredNumberIds)

    // Store settings
    accountSettings.value = settings
  }

  const setIsClientUser = (value: boolean) => {
    isClientUser.value = value
  }

  const setSessionToken = (token: string) => {
    sessionToken.value = token
  }

  const setRole = (userRole: string) => {
    const parsed = normalizeRole(userRole)
    const finalRole = VALID_ROLES.has(parsed) ? parsed : 'client_user'
    role.value = finalRole
    isClientUser.value = deriveIsClient(finalRole)
  }

  const getAccountSettings = (): any => {
    return accountSettings.value
  }

  const setAccountId = (newAccountId: string) => {
    accountId.value = newAccountId
  }

  const setInventoryId = (newInventoryIds: number[]) => {
    inventoryIds.value = newInventoryIds
  }

  const getWebsite = () => {
    return accountSettings.value?.mainWebsite ?? ''
  }

  const getAccountLogo = () => {
    return (
      accountSettings.value?.config?.accountLogo ??
      accountSettings.value?.image ??
      ''
    )
  }

  return {
    accountId,
    inventoryIds,
    isClientUser,
    sessionToken,
    role,

    getAccountSettings,
    setAccountSettings,
    setIsClientUser,
    setSessionToken,
    setRole,
    getWebsite,
    getAccountLogo,
  }
})
